import React, { useEffect } from 'react';
import { trackBookingWidgetView, trackMailtoClick } from '@/utils/analytics';
import { Clock, Mail, UserCheck } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

interface BookingWidgetProps {
  ctaPosition: string;
}

export const BookingWidget: React.FC<BookingWidgetProps> = ({ ctaPosition }) => {
  const { links } = useSiteSettings();

  useEffect(() => {
    trackBookingWidgetView(ctaPosition);
  }, [ctaPosition]);

  return (
    <div className="bg-white border border-ground-deep shadow-sm flex flex-col lg:flex-row overflow-hidden">
      {/* Reassurance Copy Sidebar */}
      <div className="lg:w-1/3 bg-ground-alt p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-ground-deep">
        <h3 className="text-2xl font-serif text-ink mb-6">Book a Discovery Call</h3>
        
        <p className="text-ink/80 mb-8 leading-relaxed">
          Let&apos;s discuss your specific goals and explore how the FIVE LENS Enneagram framework aligns with your needs. 
          <strong> There is absolutely no obligation.</strong>
        </p>

        <div className="space-y-6">
          <div className="flex gap-4">
            <Clock className="w-6 h-6 text-rule-3 shrink-0" />
            <div>
              <h4 className="font-bold text-ink text-sm">Duration</h4>
              <p className="text-sm text-ink/70">20/30/60 minutes, your choice.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <UserCheck className="w-6 h-6 text-rule-3 shrink-0" />
            <div>
              <h4 className="font-bold text-ink text-sm">Who you&apos;ll speak to</h4>
              <p className="text-sm text-ink/70">You get to choose.</p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-ground-deep">
          <h4 className="font-bold text-ink text-sm mb-2">Prefer email?</h4>
          <a 
            href={`mailto:${links.contactEmail}`} 
            onClick={() => trackMailtoClick(ctaPosition)}
            className="flex items-start gap-2 text-rule-3 hover:text-ink transition-colors text-sm font-medium"
          >
            <Mail className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="break-words flex-1">Contact us at {links.contactEmail}</span>
          </a>
        </div>
      </div>

      {/* Action Area */}
      <div className="lg:w-2/3 p-10 lg:p-16 bg-white flex flex-col justify-center items-center text-center my-auto min-h-[400px]">
        <h3 className="text-3xl font-serif text-ink mb-4">Book a discovery call</h3>
        <p className="text-ink/80 max-w-lg mb-8 leading-relaxed">
          Choose a time that suits you for a free 20-minute conversation with a senior FIVE LENS consultant. No obligation.
        </p>
        <a 
          href={links.bookingUrl || "https://outlook.office.com/book/Schedulea20minuteDiscoverySession@enneainternational.com/?ismsaljsauthenabled"}
          target="_blank" 
          rel="noopener"
          className="inline-flex items-center justify-center px-8 py-4 bg-ink text-white font-bold text-xs uppercase tracking-widest hover:bg-rule-3 transition-colors shadow-sm"
        >
          Choose a time
        </a>
      </div>
    </div>
  );
};
